import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import CreditManagementApi from '@src/apis/creditManagement';
import { COLOR } from '@src/styles/color';
import { useDebounce } from '@src/hooks';
import { StyledCreateCreditModal } from './modal.styles';

const CreateCreditModal = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    tool: '',
    displayName: '',
    creditValue: '',
    isActive: true,
  });

  const [tools, setTools] = useState([]);
  const [searchTool, setSearchTool] = useState('');
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const [loadingTools, setLoadingTools] = useState(false);

  const debouncedSearchTool = useDebounce(searchTool, 500);

  const fetchTools = async () => {
    try {
      setLoadingTools(true);
      const params = {
        searchTerm: debouncedSearchTool,
        page: 1,
        limit: 10,
      };
      const response = await CreditManagementApi.searchTools(params);
      if (response?.items) {
        setTools(response.items || []);
        setShowToolsDropdown(true);
      }
    } finally {
      setLoadingTools(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleToolSearch = (value) => {
    setSearchTool(value);
    if (value === '') {
      setFormData((prev) => ({
        ...prev,
        tool: '',
      }));
    }
  };

  const handleToolFocus = () => {
    setShowToolsDropdown(true);
    fetchTools();
  };

  const handleToolBlur = () => {
    setTimeout(() => {
      setShowToolsDropdown(false);
    }, 200);
  };

  const handleSelectTool = (tool) => {
    setFormData((prev) => ({
      ...prev,
      tool: tool.id,
    }));
    setSearchTool(
      tool.translations?.vi?.name ||
        tool.translations?.en?.name ||
        tool.name?.vi ||
        tool.name?.en ||
        '',
    );
    setShowToolsDropdown(false);
  };

  const handleStatusChange = (checked) => {
    setFormData((prev) => ({
      ...prev,
      isActive: checked,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tool) {
      newErrors.tool = t('toolRequired');
    }
    if (!formData.displayName?.trim()) {
      newErrors.displayName = t('packageNameRequired');
    }
    if (!formData.creditValue || formData.creditValue <= 0) {
      newErrors.creditValue = t('creditValueRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await CreditManagementApi.createCreditPackage(formData);

      if (response?.status) {
        enqueueSnackbar(t('createCreditPackageSuccess'), {
          variant: 'success',
        });
        onSuccess();
      }
    } catch (error) {
      enqueueSnackbar(t('createCreditPackageError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      tool: '',
      displayName: '',
      creditValue: '',
      isActive: true,
    });
    setSearchTool('');
    setTools([]);
    setShowToolsDropdown(false);
    setErrors({});
    onClose();
  };

  useEffect(() => {
    if (debouncedSearchTool.length >= 1 && showToolsDropdown) {
      fetchTools();
    }
  }, [debouncedSearchTool]);

  return (
    <StyledCreateCreditModal open={open} onClose={handleClose} maxWidth="lg">
      <DialogTitle className="dialog-title">
        {t('createCreditPackage')}
        <IconButton onClick={handleClose}>
          <FontAwesomeIcon
            icon={faXmark}
            color={COLOR.text.highlight}
            size="sm"
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className="dialog-content">
        <Box className="form-section">
          <Typography variant="h6" className="section-title">
            {t('basicInformation')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography className="field-label">
                {t('packageName')} <span className="required">*</span>
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={formData.displayName}
                onChange={(e) =>
                  handleInputChange('displayName', e.target.value)
                }
                error={!!errors.displayName}
                helperText={errors.displayName}
                placeholder={t('enterPackageName')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className="field-label">
                {t('creditValue')} <span className="required">*</span>
              </Typography>
              <TextField
                size="small"
                fullWidth
                type="number"
                value={formData.creditValue}
                onChange={(e) =>
                  handleInputChange(
                    'creditValue',
                    parseInt(e.target.value, 10) || 0,
                  )
                }
                error={!!errors.creditValue}
                helperText={errors.creditValue}
                placeholder={t('enterCreditValue')}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography className="field-label">
                {t('selectTool')} <span className="required">*</span>
              </Typography>
              <Box className="tool-search-container">
                <TextField
                  size="small"
                  fullWidth
                  value={searchTool}
                  onChange={(e) => handleToolSearch(e.target.value)}
                  onFocus={handleToolFocus}
                  onBlur={handleToolBlur}
                  error={!!errors.tool}
                  helperText={errors.tool}
                  placeholder={t('searchTools')}
                />
                {showToolsDropdown && (
                  <Box className="tool-dropdown">
                    {loadingTools && (
                      <Box p={2} textAlign="center">
                        {t('loading')}...
                      </Box>
                    )}
                    {!loadingTools &&
                      tools.length > 0 &&
                      tools.map((tool) => (
                        <Box
                          key={tool.id}
                          className="tool-option"
                          onClick={() => handleSelectTool(tool)}
                        >
                          <Typography>
                            {tool.translations?.vi?.name ||
                              tool.translations?.en?.name ||
                              tool.name?.vi ||
                              tool.name?.en ||
                              tool.id}
                          </Typography>
                        </Box>
                      ))}
                    {!loadingTools && tools.length === 0 && (
                      <Box p={2} textAlign="center" color="text.secondary">
                        {t('noDataFound')}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box className="form-section">
          <Typography variant="h6" className="section-title">
            {t('settings')}
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleStatusChange(e.target.checked)}
                />
              }
              label={t('isActive')}
            />
          </FormGroup>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="text">
          {t('cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? t('creating') : t('create')}
        </Button>
      </DialogActions>
    </StyledCreateCreditModal>
  );
};

export default CreateCreditModal;
